import { skipToken } from '@apollo/client'
import {
  Archive,
  Check,
  Database,
  FolderOpen,
  Layers,
  Network,
  Trash2,
  Zap,
} from 'lucide-react'
import { useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { z } from 'zod/v4'
import { Muted } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { RoleID, getProjectRoleLabel } from '@/lib/rbac'
import { cn } from '@/lib/utils'
import type {
  ServicesQuery,
  ServicesQueryVariables,
} from '@/routes/user-management/permission-form/index.types.gen'

export const permissionSchema = z
  .object({
    type: z.enum(['company', 'project', 'service']),
    idRole: z.string().optional(),
    serviceType: z
      .enum([
        'All',
        'App',
        'StaticSite',
        'Database',
        'ObjectStorage',
        'LoadBalancer',
      ])
      .optional(),
    capabilities: z
      .array(
        z.object({
          idApp: z.string().optional(),
          idDatabase: z.string().optional(),
          idStaticSite: z.string().optional(),
          idObjectStorage: z.string().optional(),
          idLoadBalancer: z.string().optional(),
          idRole: z.string().optional(),
        })
      )
      .optional(),
    projectCapabilities: z
      .array(
        z.object({
          idProject: z.string(),
          idRole: z.string().optional(),
        })
      )
      .optional(),
  })
  .refine((val) => !(val.type === 'company' && !val.idRole), {
    message: 'idRole is required for company type',
    path: ['idRole'],
  })
  .refine(
    (val) =>
      !(
        val.type === 'project' &&
        !val.projectCapabilities?.filter((capability) => capability.idRole)
          .length
      ),
    {
      message: 'At least one project is required for project type',
      path: ['projectCapabilities'],
    }
  )
  .refine((val) => !(val.type === 'service' && !val.capabilities?.length), {
    message: 'capabilities are required for service type',
    path: ['capabilities'],
  })

const SERVICES_QUERY = gql`
  query Services($idCompany: String!) {
    company(id: $idCompany) {
      id
      databases {
        id
        items {
          id
          displayName
        }
      }
      apps {
        id
        items {
          id
          displayName
        }
      }
      staticSites {
        id
        items {
          id
          displayName
        }
      }
      objectStorages {
        id
        items {
          id
          displayName
        }
      }
      edgeLoadBalancers {
        id
        items {
          id
          displayName
        }
      }
      projects {
        id
        items {
          id
          displayName
        }
      }
    }
  }
`

type PermissionValues = z.infer<typeof permissionSchema>
type PermissionType = PermissionValues['type']
type PermissionServiceType = NonNullable<PermissionValues['serviceType']>

export type PermissionProject = {
  id: string
  displayName: string
}

export type PermissionService = {
  __typename?: string | null
  id: string
  displayName: string
}

export type ProjectRoleOption = {
  idRole: string
  label: string
}

type Props = {
  allowedTypes?: PermissionType[]
  defaultProjectRole?: string
  projectRoleOptions?: ProjectRoleOption[]
  projects?: PermissionProject[]
  services?: PermissionService[]
}

const DEFAULT_ALLOWED_TYPES: PermissionType[] = [
  'company',
  'project',
  'service',
]

const DEFAULT_PROJECT_ROLE_OPTIONS: ProjectRoleOption[] = [
  {
    idRole: RoleID.PROJECT_DEVELOPER,
    label: getProjectRoleLabel(RoleID.PROJECT_DEVELOPER),
  },
  {
    idRole: RoleID.PROJECT_ADMIN,
    label: getProjectRoleLabel(RoleID.PROJECT_ADMIN),
  },
]

const getServiceId = (
  capability: NonNullable<PermissionValues['capabilities']>[number]
) => {
  return (
    capability.idApp ||
    capability.idDatabase ||
    capability.idStaticSite ||
    capability.idObjectStorage ||
    capability.idLoadBalancer ||
    null
  )
}

const getServiceRoleId = (serviceType: string) => {
  switch (serviceType) {
    case 'App':
      return RoleID.APP_ADMIN
    case 'Database':
      return RoleID.DATABASE_ADMIN
    case 'StaticSite':
      return RoleID.STATIC_SITE_ADMIN
    case 'ObjectStorage':
      return RoleID.OBJECT_STORAGE_ADMIN
    case 'EdgeLoadBalancer':
      return RoleID.LOAD_BALANCER_ADMIN
    default:
      return ''
  }
}

const getServiceRoleLabel = (serviceType: string) => {
  switch (serviceType) {
    case 'App':
      return 'App administrator'
    case 'Database':
      return 'Database administrator'
    case 'StaticSite':
      return 'Static site administrator'
    case 'ObjectStorage':
      return 'Object storage administrator'
    case 'EdgeLoadBalancer':
      return 'Load balancer administrator'
    default:
      return 'Service administrator'
  }
}

const getServiceTypeValue = (
  serviceType: string
): PermissionServiceType | null => {
  switch (serviceType) {
    case 'App':
    case 'StaticSite':
    case 'Database':
    case 'ObjectStorage':
      return serviceType
    case 'EdgeLoadBalancer':
      return 'LoadBalancer'
    default:
      return null
  }
}

const matchesSelectedServiceType = (
  service: PermissionService,
  serviceType: PermissionServiceType | undefined
) => {
  if (serviceType === 'All' || !serviceType) {
    return true
  }

  const normalizedServiceType = getServiceTypeValue(service.__typename ?? '')
  return normalizedServiceType === serviceType
}

export const PermissionForm = ({
  allowedTypes = DEFAULT_ALLOWED_TYPES,
  defaultProjectRole = RoleID.PROJECT_DEVELOPER,
  projectRoleOptions = DEFAULT_PROJECT_ROLE_OPTIONS,
  projects: providedProjects,
  services: providedServices,
}: Props = {}) => {
  const { idCompany } = useIdCompany()
  const [isServicePopoverOpen, setIsServicePopoverOpen] = useState(false)
  const [isProjectPopoverOpen, setIsProjectPopoverOpen] = useState(false)

  const shouldSkipContextQuery = Boolean(providedProjects && providedServices)

  const { data } = useSuspenseQuery<ServicesQuery, ServicesQueryVariables>(
    SERVICES_QUERY,
    shouldSkipContextQuery ? skipToken : { variables: { idCompany } }
  )

  const projects =
    providedProjects ??
    data?.company?.projects?.items.map((project) => ({
      id: project.id,
      displayName: project.displayName,
    })) ??
    []

  const services = providedServices ?? [
    ...(data?.company?.apps?.items ?? []),
    ...(data?.company?.staticSites?.items ?? []),
    ...(data?.company?.databases?.items ?? []),
    ...(data?.company?.objectStorages?.items ?? []),
    ...(data?.company?.edgeLoadBalancers?.items ?? []),
  ]

  const serviceCounts = {
    apps: services.filter((service) => service.__typename === 'App').length,
    staticSites: services.filter(
      (service) => service.__typename === 'StaticSite'
    ).length,
    databases: services.filter((service) => service.__typename === 'Database')
      .length,
    objectStorages: services.filter(
      (service) => service.__typename === 'ObjectStorage'
    ).length,
    loadBalancers: services.filter(
      (service) => service.__typename === 'EdgeLoadBalancer'
    ).length,
  }

  const form = useFormContext<PermissionValues>()
  const { control, watch } = form

  const { append: appendCapability, update: updateCapability } = useFieldArray({
    control,
    name: 'capabilities',
  })
  const { append: appendProject, update: updateProject } = useFieldArray({
    control,
    name: 'projectCapabilities',
  })

  const serviceType = watch('serviceType')
  const capabilities = watch('capabilities')
  const projectCapabilities = watch('projectCapabilities')

  const canSelectCompany = allowedTypes.includes('company')
  const canSelectProject =
    allowedTypes.includes('project') && projects.length > 0
  const canSelectService = allowedTypes.includes('service')

  return (
    <Form {...form}>
      <FormField
        control={form.control}
        name="type"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Access level</FormLabel>
            <FormControl>
              <Tabs value={field.value} onValueChange={field.onChange}>
                <TabsList className="w-full">
                  {canSelectCompany ? (
                    <TabsTrigger
                      value="company"
                      aria-label="Toggle company"
                      className="cursor-pointer"
                    >
                      Company
                    </TabsTrigger>
                  ) : null}
                  {canSelectProject ? (
                    <TabsTrigger
                      value="project"
                      aria-label="Toggle project"
                      className="cursor-pointer"
                    >
                      Project
                    </TabsTrigger>
                  ) : null}
                  {canSelectService ? (
                    <TabsTrigger
                      value="service"
                      aria-label="Toggle service"
                      className="cursor-pointer"
                    >
                      Service
                    </TabsTrigger>
                  ) : null}
                </TabsList>

                {canSelectCompany ? (
                  <TabsContent value="company" className="pt-3">
                    <FormField
                      control={form.control}
                      name="idRole"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              className="flex flex-col"
                            >
                              <FormItem className="flex items-start gap-3">
                                <FormControl>
                                  <RadioGroupItem
                                    value={RoleID.COMPANY_ADMIN}
                                  />
                                </FormControl>
                                <div className="flex flex-col gap-1">
                                  <FormLabel className="font-normal cursor-pointer">
                                    Company administrator
                                  </FormLabel>
                                  <FormDescription>
                                    Access to everything, manage users, see
                                    billing
                                  </FormDescription>
                                </div>
                              </FormItem>
                              <FormItem className="flex items-start gap-3">
                                <FormControl>
                                  <RadioGroupItem
                                    value={RoleID.COMPANY_DEVELOPER}
                                  />
                                </FormControl>
                                <div className="flex flex-col gap-1">
                                  <FormLabel className="font-normal cursor-pointer">
                                    Company developer
                                  </FormLabel>
                                  <FormDescription>
                                    Access to all services, cannot see billing
                                  </FormDescription>
                                </div>
                              </FormItem>
                              <FormItem className="flex items-start gap-3">
                                <FormControl>
                                  <RadioGroupItem
                                    value={RoleID.COMPANY_BILLING}
                                  />
                                </FormControl>
                                <div className="flex flex-col gap-1">
                                  <FormLabel className="font-normal cursor-pointer">
                                    Company billing
                                  </FormLabel>
                                  <FormDescription>
                                    Only see billing and company information
                                  </FormDescription>
                                </div>
                              </FormItem>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </TabsContent>
                ) : null}

                {canSelectProject ? (
                  <TabsContent value="project">
                    <div>
                      <div className="mt-3 mb-6">
                        <Popover
                          open={isProjectPopoverOpen}
                          onOpenChange={setIsProjectPopoverOpen}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={isProjectPopoverOpen}
                              className="w-full justify-between hover:bg-transparent data-[state=open]:bg-transparent"
                            >
                              <Muted className="font-normal">
                                Select project
                              </Muted>
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[var(--radix-popper-anchor-width)] p-0">
                            <Command>
                              <CommandInput
                                placeholder="Search project..."
                                className="h-9"
                              />
                              <CommandList>
                                <CommandEmpty>No project found.</CommandEmpty>
                                <CommandGroup>
                                  {projects.map((project) => {
                                    const isSelected = projectCapabilities
                                      ?.filter(
                                        (capability) => capability.idRole
                                      )
                                      .find(
                                        (capability) =>
                                          capability.idProject === project.id
                                      )

                                    return (
                                      <CommandItem
                                        className="cursor-pointer"
                                        key={project.id}
                                        disabled={Boolean(isSelected)}
                                        value={project.displayName}
                                        onSelect={() => {
                                          if (isSelected) {
                                            return
                                          }

                                          const index =
                                            projectCapabilities?.findIndex(
                                              (capability) =>
                                                capability.idProject ===
                                                project.id
                                            ) ?? -1
                                          const capability =
                                            index >= 0
                                              ? projectCapabilities?.[index]
                                              : undefined

                                          if (capability) {
                                            updateProject(index, {
                                              ...capability,
                                              idRole: defaultProjectRole,
                                            })
                                          } else {
                                            appendProject({
                                              idProject: project.id,
                                              idRole: defaultProjectRole,
                                            })
                                          }
                                        }}
                                      >
                                        <FolderOpen
                                          className="size-4"
                                          aria-hidden="true"
                                        />
                                        {project.displayName}
                                        <Check
                                          className={cn(
                                            'ml-auto',
                                            isSelected
                                              ? 'opacity-100'
                                              : 'opacity-0'
                                          )}
                                        />
                                      </CommandItem>
                                    )
                                  })}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div className="rounded-md border overflow-x-auto">
                        <Table className="min-w-[480px]">
                          <TableHeader className="top-0 bg-secondary">
                            <TableRow>
                              <TableHead className="w-1/2">
                                <div className="pl-2">Project</div>
                              </TableHead>
                              <TableHead className="w-62">Role</TableHead>
                              <TableHead className="w-12" />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {!projectCapabilities?.filter(
                              (capability) => capability.idRole
                            )?.length ? (
                              <TableRow>
                                <TableCell
                                  colSpan={3}
                                  className="text-center font-medium"
                                >
                                  <div className="mb-2 mt-2">
                                    No project selected
                                  </div>
                                </TableCell>
                              </TableRow>
                            ) : null}

                            {projectCapabilities?.map((capability, index) => {
                              const project = projects.find(
                                (item) => item.id === capability.idProject
                              )

                              if (!project) {
                                return null
                              }

                              return (
                                <TableRow
                                  key={capability.idProject}
                                  className={capability.idRole ? '' : 'hidden'}
                                >
                                  <TableCell
                                    title={project.displayName}
                                    className="font-medium"
                                  >
                                    <div className="flex h-9 items-center overflow-hidden pl-2">
                                      <span className="overflow-ellipsis whitespace-nowrap overflow-hidden">
                                        {project.displayName}
                                      </span>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex h-9 items-center">
                                      <Select
                                        value={
                                          capability.idRole ??
                                          defaultProjectRole
                                        }
                                        onValueChange={(value) => {
                                          updateProject(index, {
                                            ...capability,
                                            idRole: value,
                                          })
                                        }}
                                      >
                                        <SelectTrigger
                                          aria-label="Select project role"
                                          className="w-full"
                                        >
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {projectRoleOptions.map((option) => (
                                            <SelectItem
                                              key={option.idRole}
                                              value={option.idRole}
                                            >
                                              {option.label}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex h-9 items-center justify-end pr-2">
                                      <button
                                        type="button"
                                        className="cursor-pointer transition-colors hover:text-destructive"
                                        aria-label={`Remove ${project.displayName} permission`}
                                        onClick={() => {
                                          updateProject(index, {
                                            ...capability,
                                            idRole: undefined,
                                          })
                                        }}
                                      >
                                        <Trash2
                                          className="size-4"
                                          aria-hidden="true"
                                        />
                                      </button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </TabsContent>
                ) : null}

                {canSelectService ? (
                  <TabsContent value="service">
                    <div>
                      <div className="grid sm:grid-cols-[auto_1fr] gap-2 mt-3 mb-6">
                        <FormField
                          control={form.control}
                          name="serviceType"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Select
                                  defaultValue={field.value}
                                  onValueChange={field.onChange}
                                  value={field.value}
                                >
                                  <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Search services" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="All">
                                      All services
                                    </SelectItem>
                                    {serviceCounts.apps > 0 ? (
                                      <SelectItem value="App">
                                        Applications
                                      </SelectItem>
                                    ) : null}
                                    {serviceCounts.staticSites > 0 ? (
                                      <SelectItem value="StaticSite">
                                        Static sites
                                      </SelectItem>
                                    ) : null}
                                    {serviceCounts.databases > 0 ? (
                                      <SelectItem value="Database">
                                        Databases
                                      </SelectItem>
                                    ) : null}
                                    {serviceCounts.objectStorages > 0 ? (
                                      <SelectItem value="ObjectStorage">
                                        Object storage
                                      </SelectItem>
                                    ) : null}
                                    {serviceCounts.loadBalancers > 0 ? (
                                      <SelectItem value="LoadBalancer">
                                        Load balancers
                                      </SelectItem>
                                    ) : null}
                                  </SelectContent>
                                </Select>
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <div className="w-full">
                          <Popover
                            open={isServicePopoverOpen}
                            onOpenChange={setIsServicePopoverOpen}
                          >
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={isServicePopoverOpen}
                                className="w-full justify-between hover:bg-transparent data-[state=open]:bg-transparent"
                              >
                                <Muted className="font-normal">
                                  Select service
                                </Muted>
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[var(--radix-popper-anchor-width)] p-0">
                              <Command>
                                <CommandInput
                                  placeholder="Search service..."
                                  className="h-9"
                                />
                                <CommandList>
                                  <CommandEmpty>No service found.</CommandEmpty>
                                  <CommandGroup>
                                    {services
                                      .filter((service) =>
                                        matchesSelectedServiceType(
                                          service,
                                          serviceType
                                        )
                                      )
                                      .map((service) => {
                                        const isSelected = capabilities
                                          ?.filter(
                                            (capability) => capability.idRole
                                          )
                                          .find(
                                            (capability) =>
                                              getServiceId(capability) ===
                                              service.id
                                          )

                                        return (
                                          <CommandItem
                                            className="cursor-pointer"
                                            key={service.id}
                                            disabled={Boolean(isSelected)}
                                            value={service.displayName}
                                            onSelect={() => {
                                              if (isSelected) {
                                                return
                                              }

                                              const index =
                                                capabilities?.findIndex(
                                                  (capability) =>
                                                    getServiceId(capability) ===
                                                    service.id
                                                ) ?? -1
                                              const capability =
                                                index >= 0
                                                  ? capabilities?.[index]
                                                  : undefined
                                              const idRole = getServiceRoleId(
                                                service.__typename ?? ''
                                              )

                                              if (capability) {
                                                updateCapability(index, {
                                                  ...capability,
                                                  idRole,
                                                })
                                                return
                                              }

                                              appendCapability({
                                                idApp:
                                                  service.__typename === 'App'
                                                    ? service.id
                                                    : undefined,
                                                idDatabase:
                                                  service.__typename ===
                                                  'Database'
                                                    ? service.id
                                                    : undefined,
                                                idStaticSite:
                                                  service.__typename ===
                                                  'StaticSite'
                                                    ? service.id
                                                    : undefined,
                                                idObjectStorage:
                                                  service.__typename ===
                                                  'ObjectStorage'
                                                    ? service.id
                                                    : undefined,
                                                idLoadBalancer:
                                                  service.__typename ===
                                                  'EdgeLoadBalancer'
                                                    ? service.id
                                                    : undefined,
                                                idRole,
                                              })
                                            }}
                                          >
                                            {service.__typename === 'App' ? (
                                              <Layers className="size-4" />
                                            ) : null}
                                            {service.__typename ===
                                            'Database' ? (
                                              <Database className="size-4" />
                                            ) : null}
                                            {service.__typename ===
                                            'StaticSite' ? (
                                              <Zap className="size-4" />
                                            ) : null}
                                            {service.__typename ===
                                            'ObjectStorage' ? (
                                              <Archive className="size-4" />
                                            ) : null}
                                            {service.__typename ===
                                            'EdgeLoadBalancer' ? (
                                              <Network className="size-4" />
                                            ) : null}
                                            {service.displayName}
                                            <Check
                                              className={cn(
                                                'ml-auto',
                                                isSelected
                                                  ? 'opacity-100'
                                                  : 'opacity-0'
                                              )}
                                            />
                                          </CommandItem>
                                        )
                                      })}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>

                      <div className="rounded-md border overflow-x-auto">
                        <Table className="min-w-[480px]">
                          <TableHeader className="top-0 bg-secondary">
                            <TableRow>
                              <TableHead className="w-1/2">
                                <div className="pl-2">Service</div>
                              </TableHead>
                              <TableHead>Role</TableHead>
                              <TableHead className="w-12" />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {!capabilities?.filter(
                              (capability) => capability.idRole
                            )?.length ? (
                              <TableRow>
                                <TableCell
                                  colSpan={3}
                                  className="text-center font-medium"
                                >
                                  <div className="mb-2 mt-2">
                                    No service selected
                                  </div>
                                </TableCell>
                              </TableRow>
                            ) : null}

                            {capabilities?.map((capability, index) => {
                              const service = services.find(
                                (item) => item.id === getServiceId(capability)
                              )

                              if (!service) {
                                return null
                              }

                              return (
                                <TableRow
                                  key={service.id}
                                  className={capability.idRole ? '' : 'hidden'}
                                >
                                  <TableCell
                                    title={service.displayName}
                                    className="font-medium"
                                  >
                                    <div className="flex h-9 items-center overflow-hidden pl-2">
                                      <span className="overflow-ellipsis whitespace-nowrap overflow-hidden">
                                        {service.displayName}
                                      </span>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex h-9 items-center">
                                      {getServiceRoleLabel(
                                        service.__typename ?? ''
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex h-9 items-center justify-end pr-2">
                                      <button
                                        type="button"
                                        className="cursor-pointer transition-colors hover:text-destructive"
                                        aria-label={`Remove ${service.displayName} permission`}
                                        onClick={() => {
                                          updateCapability(index, {
                                            ...capability,
                                            idRole: undefined,
                                          })
                                        }}
                                      >
                                        <Trash2
                                          className="size-4"
                                          aria-hidden="true"
                                        />
                                      </button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </TabsContent>
                ) : null}
              </Tabs>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </Form>
  )
}
